import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { CSSProperties, forwardRef, ReactNode, Ref } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { borderColor, link, primaryText, useThemeValues } from 'styles/theme';
import { PickForStyled } from 'utils/types';

export type TagProps = Readonly<{
    /**
     * Content to display in the tag
     */
    children?: ReactNode;
    /**
     * Whether or not the tag should have a border surrounding it. Defaults to `false`.
     */
    unbordered?: boolean;
    /**
     * Whether or not this tag can be deleted. If it can be deleted, a delete
     * button will be rendered for the tag. Defaults to `false`.
     */
    deletable?: boolean;
    /**
     * Whether or not the tag has been selected, similar to a checkbox functionality
     */
    selected?: boolean;
    /**
     * Optional styles.
     */
    style?: CSSProperties;
    /**
     * Optional classname to apply to the tag
     */
    className?: string;
    /**
     * Callback that will be called when a tag is selected (clicked). If defined,
     * Tags will have the selectable behavior, similar to a checkbox.
     */
    onSelect?: () => void;
    /**
     * Callback that will be called when the tag should be deleted.
     */
    onDelete?: () => void;
}>;

const TagContainer = styled.span<
    PickForStyled<TagProps, 'unbordered' | 'selected' | 'onSelect'>
>`
    display: inline-flex;
    width: max-content;
    font-size: 1.3rem;
    align-items: center;
    justify-content: space-between;
    margin: 0.25rem 0.25rem 0.25rem 0;
    padding: 0.25rem ${({ $unbordered }) => ($unbordered ? '.25rem' : '.75rem')};
    border-radius: 4px;
    box-sizing: border-box;
    border: 1px solid ${({ $unbordered }) => ($unbordered ? 'transparent' : borderColor)};
    color: ${({ $selected }) => ($selected ? 'white' : primaryText)};
    background-color: ${({ $selected }) => ($selected ? link : 'transparent')};
    cursor: ${({ $onSelect }) => ($onSelect ? 'pointer' : 'default')};
`;

function Tag(
    {
        children,
        unbordered = false,
        deletable,
        selected,
        style,
        className,
        onSelect,
        onDelete,
    }: TagProps,
    ref: Ref<HTMLSpanElement>
) {
    const { primaryText } = useThemeValues();

    const onClickDeleteButton = (
        event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>
    ) => {
        event.preventDefault();
        if (onDelete) {
            onDelete();
        }
    };

    return (
        <TagContainer
            $unbordered={unbordered}
            $selected={selected}
            $onSelect={onSelect}
            onClick={onSelect}
            className={className}
            style={style}
            ref={ref}
        >
            {children}
            {deletable && (
                <HoverIconButton
                    title="Delete"
                    ariaLabel="Delete"
                    icon={faTimes}
                    hoverIcon={faTimes}
                    color={primaryText}
                    onClick={onClickDeleteButton}
                />
            )}
        </TagContainer>
    );
}

const ForwardRefTag = forwardRef(Tag);

export { ForwardRefTag as Tag };
